import { defaultValidators } from './modules';
import { useRoutes } from '@/composables/useRoutes';
import { useKyc } from '@/composables/useKyc';

const defaultConfig: Containment.Context = {
  global: [],
};

export class ContainmentInstance {
  private static instance: ContainmentInstance;
  private static config: Containment.Context;
  private static state: Ref<Containment.State> = ref({});

  private currentContextState: Ref<keyof Containment.Context | null> =
    ref(null);

  private currentModulesPending: Ref<Partial<
    Record<keyof Containment.Context, Containment.Validation | null>
  > | null> = ref(null);

  private currentPendencyState: Ref<Containment.Validation | null> = ref(null);

  private currentQueue?: Containment.Queue;
  private isModalOpen: Ref<boolean> = ref(false);

  public static getInstance(): ContainmentInstance {
    if (!ContainmentInstance.instance) {
      ContainmentInstance.instance = new ContainmentInstance();
    }

    return ContainmentInstance.instance;
  }

  constructor(config?: Containment.Context) {
    this.updateConfig(config || defaultConfig);
  }

  /**
   * Returns current context list state.
   * @returns Containment.State
   */
  public get state() {
    return ContainmentInstance.state.value;
  }

  /**
   * Updates instance context state
   * @param config Containment.Context
   * @returns void
   */
  public async updateState(): Promise<void> {
    if (!this.config) {
      return;
    }

    const alreadyValidatedModules: Containment.PendencyName[] = [];

    for (const configModule in this.config) {
      const contextName = configModule as keyof Containment.Context;

      const validationModules = this.getValidationModulesFromScope(contextName);

      const validationState = await this.checkValidationModules(
        validationModules,
        async ({ name, frequent, force }, validator) => {
          const shouldSkipValidation =
            !frequent && !force && alreadyValidatedModules.includes(name);

          if (shouldSkipValidation) {
            return { module: name, isValid: true };
          }

          const isValid = await validator();

          if (!frequent && !force && isValid) {
            alreadyValidatedModules.push(name);
          }

          return { module: name, isValid };
        },
      );

      const state = {
        [contextName]: validationState,
      };

      ContainmentInstance.state.value = {
        ...ContainmentInstance.state.value,
        ...state,
      };
    }
  }

  /**
   * Returns current setting parameters.
   * @returns Containment.Context
   */
  private get config() {
    return ContainmentInstance.config;
  }

  /**
   * Updates instance configs
   * @param config Containment.Context
   * @returns void
   */
  public updateConfig(config: Containment.Context): void {
    ContainmentInstance.config = { ...ContainmentInstance.config, ...config };

    this.updateState();
  }

  private get isQueueEmpty(): boolean {
    return !this.currentQueue?.modules.length;
  }

  private executeCallback(): void {
    if (!this.currentQueue || !this.isQueueEmpty) {
      return;
    }

    if (this.currentQueue.callback) {
      this.currentQueue.callback();
      this.currentQueue = undefined;
    }
  }

  private async defaultPendingModulesIterator<
    IteratorType = Containment.Validation,
  >(
    validationData: Containment.Validation,
    validator: () => Promise<boolean>,
  ): Promise<IteratorType | void> {
    const isValid = await validator();

    const { name, customValidator, force, frequent, ...rest } = validationData;

    // Special rule: allow skipping `contact` even when frequent, if it's already valid
    if (name === 'contact' && isValid && !force) {
      return;
    }

    if (isValid && !force && !frequent) {
      return;
    }

    return { name, customValidator, ...rest } as IteratorType;
  }

  /**
   * Discovery validation modules and return a list with names them.
   * @param validationModules
   * @returns ModuleName[]
   */
  private async checkValidationModules<IteratorType = Containment.Validation>(
    validationModules?: Containment.Validation[],
    iterator?: typeof this.defaultPendingModulesIterator<IteratorType>,
  ): Promise<IteratorType[]> {
    if (!validationModules) {
      throw new TypeError('containment: No validation module');
    }

    if (!Array.isArray(validationModules)) {
      throw new TypeError('containment: Invalid module config');
    }

    const pendingModuleList: IteratorType[] = [];

    const callback = iterator || this.defaultPendingModulesIterator;

    for (const validationModule of validationModules) {
      const { name, customValidator, frequent, force } = validationModule;

      // `contact` always uses its default validator regardless of frequent/force
      const validator =
        customValidator ??
        ((!frequent && !force) || name === 'contact'
          ? defaultValidators[name]
          : () => false);

      const returnedData = await callback(validationModule, validator);

      if (!returnedData) {
        continue;
      }

      pendingModuleList.push(returnedData);
    }

    return pendingModuleList;
  }

  /**
   * Finder for scope validaton modules and returns it when found or default value from globalModules
   * @param scope Containment.Scope
   * @returns Containment.Validation[]
   */
  private getValidationModulesFromScope(
    scope: Containment.Scope,
  ): Containment.Validation[] {
    if (!scope) {
      throw new Error('Wrong implementation of ContainmentInstance');
    }

    if (Array.isArray(scope)) {
      return scope;
    }

    if (this.config[scope]) {
      return this.config[scope];
    } else {
      return [];
    }
  }

  private clearPendencyModule(scope: Containment.Scope): void {
    if (!scope) {
      return;
    }

    this.currentPendencyState.value = null;
    this.currentContextState.value = null;

    if (typeof scope !== 'string') {
      return;
    }

    if (!this.currentModulesPending.value) {
      return;
    }

    this.currentModulesPending.value[scope] = null;
  }

  private assignPendencyModule(
    scope: Containment.Scope,
    pendency: Containment.Validation,
  ): void {
    if (!scope || !pendency) {
      return;
    }

    this.currentPendencyState.value = pendency;

    if (typeof scope !== 'string') {
      return;
    }

    this.currentContextState.value = scope;

    if (!this.currentModulesPending.value) {
      this.currentModulesPending.value = {
        [scope]: pendency,
      };
    } else {
      this.currentModulesPending.value[scope] = pendency;
    }
  }

  /**
   * If exists, returns the current pending step.
   * @returns ModuleName | undefined
   */
  public async fetchCurrentPending(
    scope: Containment.Scope,
  ): Promise<Containment.Validation | undefined> {
    try {
      const validationModules = this.getValidationModulesFromScope(scope);

      const pendingModuleList =
        await this.checkValidationModules(validationModules);

      this.currentQueue = {
        ...this.currentQueue,
        modules: pendingModuleList,
      };

      if (!Array.isArray(pendingModuleList) || !pendingModuleList.length) {
        this.clearPendencyModule(scope);

        return;
      }

      const currentPendency = pendingModuleList[0];

      if (currentPendency) {
        this.assignPendencyModule(scope, currentPendency);
      }

      this.updateState();

      return currentPendency;
    } catch (error) {
      console.error('containment: get currentPending error\n', error);
    }
  }

  /**
   * Redirects user to contention page.
   */
  private async redirectToContentionPage(): Promise<
    ReturnType<typeof goToLocaleRoute>
  > {
    const { goToLocaleRoute } = useRoutes();

    return await goToLocaleRoute({
      name: 'pending',
      replace: true,
    });
  }

  public get currentPendency(): Containment.Validation['name'] | null {
    return this.currentPendencyState.value?.name || null;
  }

  public get currentContext(): keyof Containment.Context | null {
    return this.currentContextState.value || null;
  }

  public get currentContextModules(): Containment.Validation[] | null {
    if (!this.currentContext) {
      return null;
    }

    return this.getValidationModulesFromScope(this.currentContext);
  }

  public get currentContextValidationState():
    | Containment.ValidationState[]
    | null {
    if (!this.currentContext || !this.state) {
      return null;
    }

    return this.state?.[this.currentContext] || null;
  }

  public hasPendencyOn(contextName: keyof Containment.Context): boolean {
    if (!contextName) {
      return false;
    }

    if (!this.currentModulesPending.value) {
      return false;
    }

    return !!this.currentModulesPending.value[contextName];
  }

  public getPendenciesFrom(contextName: keyof Containment.Context) {
    if (!contextName) {
      return null;
    }

    if (!this.currentModulesPending.value) {
      return null;
    }

    if (!Array.isArray(this.currentModulesPending.value[contextName])) {
      return null;
    }

    const [currentPendency] = this.currentModulesPending.value[contextName];

    return currentPendency;
  }

  public get isVerificationModalOpen(): boolean {
    return this.isModalOpen.value;
  }

  public openVerificationModal(scope: Containment.Scope): void {
    if (!scope) {
      return;
    }

    if (!this.currentPendency) {
      this.isModalOpen.value = false;
    }

    if (typeof scope === 'string') {
      const { $kyc } = useNuxtApp();

      $kyc.setSource(scope);
      useKyc().kycSource.value = scope;
    }

    this.isModalOpen.value = true;
  }

  public closeVerificationModal(): void {
    this.isModalOpen.value = false;
  }

  /**
   * Handle a step verification before start callback function
   * @param scope Containment.Scope
   * @param callback
   * @returns void
   */
  public async handle(
    scope: Containment.Scope,
    callback?: () => void,
    options: Containment.HandleOptions = {},
  ): Promise<void> {
    try {
      if (!scope) {
        return;
      }

      if (typeof scope !== 'string') {
        this.currentContextState.value = null;
      }

      const validationModules = this.getValidationModulesFromScope(scope);

      if (
        (!validationModules || !validationModules.length) &&
        scope === 'validation_code_sms'
      ) {
        const { openToaster } = useLayoutStore();

        openToaster({
          message:
            'Indisponível no momento. A validação não está habilitada, gentileza entrar em contato com o suporte',
          type: 'error',
        });

        return;
      }

      this.currentQueue = {
        modules: validationModules,
        callback,
      };

      await this.fetchCurrentPending(scope);

      if (this.isQueueEmpty) {
        return this.executeCallback();
      }

      if (typeof scope === 'string') {
        if (!this.hasPendencyOn(scope)) {
          return this.executeCallback();
        }

        const { $kyc } = useNuxtApp();

        useKyc().kycSource.value = scope;
        $kyc.setSource(scope);
      }

      const { action = 'modal' } = options;

      if (action === 'modal') {
        this.openVerificationModal(scope);
      }

      if (action === 'redirect') {
        await this.redirectToContentionPage();
      }
    } catch (error) {
      console.error('containment: handle error\n', error);
    }
  }

  public async solve(pendingModule: Containment.Validation) {
    if (!this.currentQueue) {
      return;
    }

    if (this.isQueueEmpty) {
      return;
    }

    const isValidEntry = this.currentQueue.modules.some(
      ({ name }) => name === pendingModule.name,
    );

    if (!isValidEntry) {
      return;
    }

    if (pendingModule.customValidator) {
      this.currentQueue.modules = this.currentQueue.modules.map((pendency) => {
        if (pendency.name === pendingModule.name) {
          return {
            ...pendency,
            customValidator: pendingModule.customValidator,
            force: false,
          };
        }

        return pendency;
      });
    }

    const currentPending = await this.fetchCurrentPending(
      this.currentQueue.modules,
    );

    if (!currentPending && this.isQueueEmpty) {
      this.executeCallback();
      this.closeVerificationModal();
    }
  }
}
